import TEXT, {
  type Text as SystemMetadataFieldsText,
} from './generated/TEXT/systemMetadataFields';

export const SYSTEM_FIELD_KEYS = {
  ALPHA_CHANNEL: 'Alpha Channel',
  ASSIGNEE: 'Assignee',
  APPROVAL_STATUS: 'Approval Status',
  ASSET_ID: 'Asset ID',
  ARCHIVED: 'Archived',
  BIT_RATE: 'Bit Rate',
  AUDIO_BIT_DEPTH: 'Audio Bit Depth',
  AUDIO_BIT_RATE: 'Audio Bit Rate',
  AUDIO_CHANNELS: 'Audio Channels',
  AUDIO_CODEC: 'Audio Codec',
  AUDIO_SAMPLE_RATE: 'Audio Sample Rate',
  C2C_CONNECTION: 'C2C Connection',
  CHECKSUM: 'Checksum',
  COLOR_PROFILE: 'Color Profile',
  COLOR_SPACE: 'Color Space',
  COMMENT_COUNT: 'Comment Count',
  DATE_MODIFIED: 'Date Modified',
  DATE_UPLOADED: 'Date Uploaded',
  DURATION: 'Duration',
  DYNAMIC_RANGE: 'Dynamic Range',
  END_TIME: 'End Time',
  FILE_SIZE: 'File Size',
  FILE_TYPE: 'File Type',
  FORMAT: 'Format',
  FRAME_RATE: 'Frame Rate',
  KEYWORDS: 'Keywords',
  LAST_MODIFIED_BY: 'Last Modified By',
  MATERIAL_COUNT: '3D Material Count',
  MESH_COUNT: '3D Mesh Count',
  MODEL: 'Model',
  NAME: 'Name',
  NOTES: 'Notes',
  PAGE_COUNT: 'Page Count',
  PHYSICAL_SIZE: '3D Physical Size',
  POLY_COUNT: '3D Poly Count',
  PROMPT: 'Prompt',
  RATING: 'Rating',
  RESOLUTION_HEIGHT: 'Resolution - Height',
  RESOLUTION_WIDTH: 'Resolution - Width',
  SEEN_BY: 'Seen By',
  SOURCE_FILENAME: 'Source Filename',
  START_TIME: 'Start Time',
  STATUS: 'Status',
  TEXTURE_COUNT: '3D Texture Count',
  TRANSCRIPT: 'Transcript',
  UPLOADED_VIA: 'Uploaded Via',
  UPLOADER: 'Uploader',
  VERSION_COUNT: 'Version Count',
  VIDEO_BIT_RATE: 'Video Bit Rate',
  VIDEO_CODEC: 'Video Codec',
  VISUAL_BIT_DEPTH: 'Visual Bit Depth',
} as const;

export type SystemFieldKey =
  (typeof SYSTEM_FIELD_KEYS)[keyof typeof SYSTEM_FIELD_KEYS];

TEXT.DEFINE({
  /** Metadata field label for whether the asset has an alpha channel. */
  alphaChannel: 'Alpha Channel',
  /** Metadata field label for the assignee of the asset. */
  assignee: 'Assignee',
  /** Metadata field label for the asset's approval status. */
  approvalStatus: 'Approval Status',
  /** Metadata field label for the asset's unique identifier. */
  assetId: 'Asset ID',
  /** Metadata field label for whether the asset is archived. */
  archived: 'Archived',
  /** Metadata field label for the overall (combined audio + video) bit rate of the asset. */
  bitRate: 'Bit Rate',
  /** Metadata field label for the audio bit depth. */
  audioBitDepth: 'Audio Bit Depth',
  /** Metadata field label for the audio stream bit rate. */
  audioBitRate: 'Audio Bit Rate',
  /** Metadata field label for the number of audio channels. */
  audioChannels: 'Audio Channels',
  /** Metadata field label for the audio codec. */
  audioCodec: 'Audio Codec',
  /** Metadata field label for the audio sample rate. */
  audioSampleRate: 'Audio Sample Rate',
  /** Metadata field label for the Camera to Cloud connection linked to the asset. */
  c2cConnection: 'C2C Connection',
  /** Metadata field label for the file checksum. */
  checksum: 'Checksum',
  /** Metadata field label for the embedded color profile. */
  colorProfile: 'Color Profile',
  /** Metadata field label for the asset color space. */
  colorSpace: 'Color Space',
  /** Metadata field label for the number of comments on the asset. */
  commentCount: 'Comment Count',
  /** Metadata field label for when the asset was last modified. */
  dateModified: 'Date Modified',
  /** Metadata field label for when the asset was uploaded. */
  dateUploaded: 'Date Uploaded',
  /** Metadata field label for the media duration. */
  duration: 'Duration',
  /** Metadata field label for the media dynamic range, such as SDR or HDR. */
  dynamicRange: 'Dynamic Range',
  /** Metadata field label for the media end timecode. */
  endTime: 'End Time',
  /** Metadata field label for the file size. */
  fileSize: 'File Size',
  /** Metadata field label for the high-level file type, such as video or image. */
  fileType: 'File Type',
  /** Metadata field label for the media format. */
  format: 'Format',
  /** Metadata field label for the video frame rate. */
  frameRate: 'Frame Rate',
  /** Metadata field label for the keywords associated with the asset. */
  keywords: 'Keywords',
  /** Metadata field label for the person who last modified the asset. */
  lastModifiedBy: 'Last Modified By',
  /** Metadata field label for the number of materials in a 3D asset. */
  materialCount: '3D Material Count',
  /** Metadata field label for the number of meshes in a 3D asset. */
  meshCount: '3D Mesh Count',
  /** Metadata field label for the AI model used to generate the asset. */
  model: 'Model',
  /** Metadata field label for the asset name. */
  name: 'Name',
  /** Metadata field label for notes on the asset. */
  notes: 'Notes',
  /** Metadata field label for the number of pages in a document asset. */
  pageCount: 'Page Count',
  /** Metadata field label for the physical dimensions of a 3D asset. */
  physicalSize: '3D Physical Size',
  /** Metadata field label for the polygon count of a 3D asset. */
  polyCount: '3D Poly Count',
  /** Metadata field label for the text prompt used to generate the asset. */
  prompt: 'Prompt',
  /** Metadata field label for the star rating of the asset. */
  rating: 'Rating',
  /** Metadata field label for the pixel height of the asset. */
  resolutionHeight: 'Resolution - Height',
  /** Metadata field label for the pixel width of the asset. */
  resolutionWidth: 'Resolution - Width',
  /** Metadata field label for the people who have viewed the asset. */
  seenBy: 'Seen By',
  /** Metadata field label for the asset's original filename. */
  sourceFilename: 'Source Filename',
  /** Metadata field label for the media start timecode. */
  startTime: 'Start Time',
  /** Metadata field label for the status of the asset. */
  status: 'Status',
  /** Metadata field label for the number of textures in a 3D asset. */
  textureCount: '3D Texture Count',
  /** Metadata field label for whether a transcript is available for the asset. */
  transcript: 'Transcript',
  /** Metadata field label for the source or method used to upload the asset. */
  uploadedVia: 'Uploaded Via',
  /** Metadata field label for the person who uploaded the asset. */
  uploader: 'Uploader',
  /** Metadata field label for the number of versions of the asset. */
  versionCount: 'Version Count',
  /** Metadata field label for the video stream bit rate. */
  videoBitRate: 'Video Bit Rate',
  /** Metadata field label for the video codec. */
  videoCodec: 'Video Codec',
  /** Metadata field label for the visual bit depth. */
  visualBitDepth: 'Visual Bit Depth',
});

export const SYSTEM_FIELD_TEXT_KEYS: Record<
  SystemFieldKey,
  keyof SystemMetadataFieldsText
> = {
  [SYSTEM_FIELD_KEYS.ALPHA_CHANNEL]: 'alphaChannel',
  [SYSTEM_FIELD_KEYS.ASSIGNEE]: 'assignee',
  [SYSTEM_FIELD_KEYS.APPROVAL_STATUS]: 'approvalStatus',
  [SYSTEM_FIELD_KEYS.ASSET_ID]: 'assetId',
  [SYSTEM_FIELD_KEYS.ARCHIVED]: 'archived',
  [SYSTEM_FIELD_KEYS.BIT_RATE]: 'bitRate',
  [SYSTEM_FIELD_KEYS.AUDIO_BIT_DEPTH]: 'audioBitDepth',
  [SYSTEM_FIELD_KEYS.AUDIO_BIT_RATE]: 'audioBitRate',
  [SYSTEM_FIELD_KEYS.AUDIO_CHANNELS]: 'audioChannels',
  [SYSTEM_FIELD_KEYS.AUDIO_CODEC]: 'audioCodec',
  [SYSTEM_FIELD_KEYS.AUDIO_SAMPLE_RATE]: 'audioSampleRate',
  [SYSTEM_FIELD_KEYS.C2C_CONNECTION]: 'c2cConnection',
  [SYSTEM_FIELD_KEYS.CHECKSUM]: 'checksum',
  [SYSTEM_FIELD_KEYS.COLOR_PROFILE]: 'colorProfile',
  [SYSTEM_FIELD_KEYS.COLOR_SPACE]: 'colorSpace',
  [SYSTEM_FIELD_KEYS.COMMENT_COUNT]: 'commentCount',
  [SYSTEM_FIELD_KEYS.DATE_MODIFIED]: 'dateModified',
  [SYSTEM_FIELD_KEYS.DATE_UPLOADED]: 'dateUploaded',
  [SYSTEM_FIELD_KEYS.DURATION]: 'duration',
  [SYSTEM_FIELD_KEYS.DYNAMIC_RANGE]: 'dynamicRange',
  [SYSTEM_FIELD_KEYS.END_TIME]: 'endTime',
  [SYSTEM_FIELD_KEYS.FILE_SIZE]: 'fileSize',
  [SYSTEM_FIELD_KEYS.FILE_TYPE]: 'fileType',
  [SYSTEM_FIELD_KEYS.FORMAT]: 'format',
  [SYSTEM_FIELD_KEYS.FRAME_RATE]: 'frameRate',
  [SYSTEM_FIELD_KEYS.KEYWORDS]: 'keywords',
  [SYSTEM_FIELD_KEYS.LAST_MODIFIED_BY]: 'lastModifiedBy',
  [SYSTEM_FIELD_KEYS.MATERIAL_COUNT]: 'materialCount',
  [SYSTEM_FIELD_KEYS.MESH_COUNT]: 'meshCount',
  [SYSTEM_FIELD_KEYS.MODEL]: 'model',
  [SYSTEM_FIELD_KEYS.NAME]: 'name',
  [SYSTEM_FIELD_KEYS.NOTES]: 'notes',
  [SYSTEM_FIELD_KEYS.PAGE_COUNT]: 'pageCount',
  [SYSTEM_FIELD_KEYS.PHYSICAL_SIZE]: 'physicalSize',
  [SYSTEM_FIELD_KEYS.POLY_COUNT]: 'polyCount',
  [SYSTEM_FIELD_KEYS.PROMPT]: 'prompt',
  [SYSTEM_FIELD_KEYS.RATING]: 'rating',
  [SYSTEM_FIELD_KEYS.RESOLUTION_HEIGHT]: 'resolutionHeight',
  [SYSTEM_FIELD_KEYS.RESOLUTION_WIDTH]: 'resolutionWidth',
  [SYSTEM_FIELD_KEYS.SEEN_BY]: 'seenBy',
  [SYSTEM_FIELD_KEYS.SOURCE_FILENAME]: 'sourceFilename',
  [SYSTEM_FIELD_KEYS.START_TIME]: 'startTime',
  [SYSTEM_FIELD_KEYS.STATUS]: 'status',
  [SYSTEM_FIELD_KEYS.TEXTURE_COUNT]: 'textureCount',
  [SYSTEM_FIELD_KEYS.TRANSCRIPT]: 'transcript',
  [SYSTEM_FIELD_KEYS.UPLOADED_VIA]: 'uploadedVia',
  [SYSTEM_FIELD_KEYS.UPLOADER]: 'uploader',
  [SYSTEM_FIELD_KEYS.VERSION_COUNT]: 'versionCount',
  [SYSTEM_FIELD_KEYS.VIDEO_BIT_RATE]: 'videoBitRate',
  [SYSTEM_FIELD_KEYS.VIDEO_CODEC]: 'videoCodec',
  [SYSTEM_FIELD_KEYS.VISUAL_BIT_DEPTH]: 'visualBitDepth',
};

/**
 * Maps legacy backend field names (pre-rename) to their canonical system field
 * key. Used as a fallback when older field definitions still use the previous
 * raw name.
 */
export const LEGACY_SYSTEM_FIELD_KEY_BY_NAME = new Map<string, SystemFieldKey>([
  ['Bit Depth', SYSTEM_FIELD_KEYS.VISUAL_BIT_DEPTH],
  ['Channels', SYSTEM_FIELD_KEYS.AUDIO_CHANNELS],
  ['Pages', SYSTEM_FIELD_KEYS.PAGE_COUNT],
  ['Sample Rate', SYSTEM_FIELD_KEYS.AUDIO_SAMPLE_RATE],
  ['Material Count', SYSTEM_FIELD_KEYS.MATERIAL_COUNT],
  ['Mesh Count', SYSTEM_FIELD_KEYS.MESH_COUNT],
  ['Physical Size', SYSTEM_FIELD_KEYS.PHYSICAL_SIZE],
  ['Poly Count', SYSTEM_FIELD_KEYS.POLY_COUNT],
  ['Texture Count', SYSTEM_FIELD_KEYS.TEXTURE_COUNT],
]);
